import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Globe, ExternalLink, CheckCircle, XCircle, Lock, AlertTriangle } from "lucide-react";

// Portal scraper status - which ones actually work
const SCRAPER_STATUS = {
  "Subito.it": { status: "working", label: "Funzionante", detail: "Ricerca per comune" },
  "CasaDaPrivato.it": { status: "working", label: "Funzionante", detail: "Ricerca provincia MB" },
  "ClickCase.it": { status: "working", label: "Funzionante", detail: "Solo privati" },
  "Casadasoli.it": { status: "working", label: "Funzionante", detail: "Solo privati" },
  "Dove.it": { status: "working", label: "Funzionante", detail: "Ricerca provincia" },
  "Vendocasaprivato.it": { status: "working", label: "Funzionante", detail: "Solo privati (pochi annunci)" },
  "Idealista": { status: "working", label: "Funzionante", detail: "ZenRows — privati" },
  "Immobiliare.it": { status: "working", label: "Funzionante", detail: "ZenRows — solo privati" },
  "Casa.it": { status: "working", label: "Funzionante", detail: "ZenRows" },
  "Bakeca.it": { status: "working", label: "Funzionante", detail: "ZenRows" },
  "VendereCasa.com": { status: "working", label: "Funzionante", detail: "ZenRows — solo privati" },
  "Facebook Marketplace": { status: "pending", label: "Parziale", detail: "Cookie session — paginazione limitata" },
};

const STATUS_ICONS = {
  working: { icon: CheckCircle, color: "text-emerald-600", bg: "bg-emerald-50", border: "border-emerald-200" },
  pending: { icon: AlertTriangle, color: "text-amber-600", bg: "bg-amber-50", border: "border-amber-200" },
  blocked: { icon: Lock, color: "text-red-500", bg: "bg-red-50", border: "border-red-200" },
};

const TYPE_COLORS = {
  "Specialistico": "bg-violet-100 text-violet-700",
  "Generalista": "bg-sky-100 text-sky-700",
  "Social": "bg-rose-100 text-rose-700",
  "Proptech": "bg-teal-100 text-teal-700",
  "Solo privati": "bg-orange-100 text-orange-700",
  "Solo agenzie": "bg-red-100 text-red-600",
  "Misto": "bg-zinc-100 text-zinc-600",
  "Aggregatore": "bg-indigo-100 text-indigo-600",
};

export default function PortalManagement({ portals, onToggle }) {
  const working = portals.filter(p => SCRAPER_STATUS[p.name]?.status === "working");
  const pending = portals.filter(p => SCRAPER_STATUS[p.name]?.status === "pending");
  const blocked = portals.filter(p => SCRAPER_STATUS[p.name]?.status === "blocked");
  const noScraper = portals.filter(p => !SCRAPER_STATUS[p.name]);

  const sections = [
    { title: "Funzionanti", portals: working, status: "working", count: working.length },
    { title: "In attesa", portals: pending, status: "pending", count: pending.length },
    { title: "Bloccati (serve proxy)", portals: blocked, status: "blocked", count: blocked.length },
    { title: "Altri portali", portals: noScraper, status: "blocked", count: noScraper.length },
  ].filter(s => s.portals.length > 0);

  return (
    <div data-testid="portal-management-page" className="p-4 sm:p-6 space-y-6">
      <div>
        <h1 className="font-heading text-2xl sm:text-4xl font-black text-zinc-900 tracking-tighter">Portali</h1>
        <p className="text-xs sm:text-sm text-zinc-500 mt-0.5">
          {working.length} funzionanti su {portals.length} totali
        </p>
      </div>

      {/* Summary badges */}
      <div className="flex flex-wrap gap-2">
        <span className="px-3 py-1.5 rounded-sm text-xs font-bold bg-emerald-100 text-emerald-700 border border-emerald-200">
          {working.length} Funzionanti
        </span>
        {pending.length > 0 && (
          <span className="px-3 py-1.5 rounded-sm text-xs font-bold bg-amber-100 text-amber-700 border border-amber-200">
            {pending.length} In attesa
          </span>
        )}
        <span className="px-3 py-1.5 rounded-sm text-xs font-bold bg-red-100 text-red-600 border border-red-200">
          {blocked.length + noScraper.length} Bloccati/N.A.
        </span>
      </div>

      {sections.map(section => {
        const statusCfg = STATUS_ICONS[section.status] || STATUS_ICONS.blocked;

        return (
          <div key={section.title} className="space-y-2">
            <div className="flex items-center gap-2">
              <statusCfg.icon className={`w-4 h-4 ${statusCfg.color}`} />
              <span className="text-xs tracking-wider uppercase font-semibold text-zinc-500">
                {section.title} ({section.count})
              </span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {section.portals.map(portal => {
                const scraperInfo = SCRAPER_STATUS[portal.name];
                const statusStyle = STATUS_ICONS[scraperInfo?.status] || STATUS_ICONS.blocked;
                const StatusIcon = statusStyle.icon;

                return (
                  <div
                    key={portal.id}
                    data-testid={`portal-card-${portal.id}`}
                    className={`border rounded-sm p-3 transition-all duration-200 hover:shadow-sm ${
                      portal.active ? `${statusStyle.bg} ${statusStyle.border}` : "bg-zinc-50 border-zinc-200 opacity-50"
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2.5 min-w-0">
                        <StatusIcon className={`w-4 h-4 shrink-0 ${statusStyle.color}`} />
                        <div className="min-w-0">
                          <h3 className="font-heading text-sm font-bold text-zinc-900 truncate">{portal.name}</h3>
                          <div className="flex items-center gap-1.5 mt-0.5">
                            <span className={`px-1.5 py-0 rounded-sm text-[9px] font-semibold ${TYPE_COLORS[portal.type] || TYPE_COLORS["Misto"]}`}>
                              {portal.type}
                            </span>
                            {scraperInfo && (
                              <span className="text-[9px] text-zinc-400">{scraperInfo.detail}</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <Switch
                        data-testid={`portal-toggle-${portal.id}`}
                        checked={portal.active}
                        onCheckedChange={() => onToggle(portal.id)}
                      />
                    </div>
                    <div className="flex items-center justify-between mt-2 pt-2 border-t border-zinc-100/50">
                      <span className={`text-[10px] font-semibold ${portal.active ? statusStyle.color : "text-zinc-400"}`}>
                        {portal.active ? (scraperInfo?.label || "Attivo") : "Disattivato"}
                      </span>
                      <a href={portal.url} target="_blank" rel="noopener noreferrer" className="text-[10px] text-zinc-400 hover:text-zinc-700 flex items-center gap-0.5">
                        Visita <ExternalLink className="w-2.5 h-2.5" />
                      </a>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
